import i18n from '@/i18n.json';

/*
 * What the app-level error boundary shows (see chunk-load-recovery.js).
 *
 * The boundary in _app.js sits OUTSIDE NextIntlClientProvider, the colour-mode
 * provider and GlobalStyle, and once it trips those are unmounted. So this file
 * must stay free of context: no next-intl, no styled-components, no theme, no
 * router, no design tokens (the CSS variables are gone with GlobalStyle).
 * Inline styles, literal colours and English copy only. A fallback that throws
 * leaves the shopper with a blank page.
 */

const INK = '#4c4c71';
const INDIGO = '#4b4df1';
const FONT = 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

const styles = {
  // Covers the viewport with its own surface: legible whatever theme class is left on <html>.
  screen: {
    alignItems: 'center',
    background: '#ffffff',
    boxSizing: 'border-box',
    color: INK,
    display: 'flex',
    fontFamily: FONT,
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '64px 24px',
    textAlign: 'center',
  },
  content: {
    maxWidth: 480,
    width: '100%',
  },
  title: {
    color: INK,
    fontFamily: FONT,
    fontSize: 30,
    fontWeight: 600,
    letterSpacing: '-0.025em',
    lineHeight: '36px',
    margin: 0,
  },
  message: {
    color: '#6b6b8c',
    fontSize: 16,
    lineHeight: '24px',
    margin: '16px 0 0',
  },
  actions: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'center',
    marginTop: 32,
  },
  action: {
    alignItems: 'center',
    border: `1px solid ${INDIGO}`,
    borderRadius: 9999,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'inline-flex',
    fontFamily: FONT,
    fontSize: 16,
    fontWeight: 600,
    justifyContent: 'center',
    lineHeight: '24px',
    minHeight: 44,
    padding: '9px 24px',
    textDecoration: 'none',
  },
  primary: {
    background: INDIGO,
    color: '#ffffff',
  },
  secondary: {
    background: '#ffffff',
    color: INDIGO,
  },
};

/** "/" or "/<locale>/", read from the address bar: there is no router to ask here. */
export function homeHref() {
  try {
    const [first] = window.location.pathname.split('/').filter(Boolean);
    if (first && first !== i18n.defaultLocale && i18n.locales.includes(first)) {
      return `/${first}`;
    }
  } catch {
    // No window (server) or an unreadable location: the default home is fine.
  }

  return '/';
}

function reloadPage() {
  window.location.reload();
}

/**
 * Context-free message screen.
 *
 * @param {string} title
 * @param {string} [message]
 * @param {string} [titleId]      id for the heading (aria-labelledby targets)
 * @param {string} [reloadLabel]  shows a reload button when set
 * @param {Function} [onReload]   defaults to a plain page reload
 * @param {string} [homeLabel]    shows a plain link to the home page when set
 */
export function FallbackScreen({ homeLabel, message, onReload, reloadLabel, title, titleId }) {
  return (
    <div data-error-fallback="" role="alert" style={styles.screen}>
      <div style={styles.content}>
        <h1 id={titleId} style={styles.title}>
          {title}
        </h1>
        {message ? <p style={styles.message}>{message}</p> : null}
        {reloadLabel || homeLabel ? (
          <div style={styles.actions}>
            {reloadLabel ? (
              <button
                onClick={onReload || reloadPage}
                style={{ ...styles.action, ...styles.primary }}
                type="button"
              >
                {reloadLabel}
              </button>
            ) : null}
            {homeLabel ? (
              // A plain anchor on purpose: a full page load is the way out of a crashed app.
              <a href={homeHref()} style={{ ...styles.action, ...styles.secondary }}>
                {homeLabel}
              </a>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** The app crashed while rendering. */
export function ErrorFallback() {
  return (
    <FallbackScreen
      homeLabel="Go to the home page"
      message="The page didn't load. Reload it to try again, or go back to the home page."
      reloadLabel="Reload page"
      title="Something went wrong"
    />
  );
}

export default ErrorFallback;
